/**
 * Task Hooks
 * 
 * React hooks for workflow task operations
 */

import { useCallback, useEffect, useState } from 'react';
import { EscalationRule, TaskMetrics, taskRepository, Task } from '../repositories/TaskRepository';

// Hook for fetching all tasks
export function useTasks(options?: {
    status?: string;
    assignedTo?: string;
    instanceId?: string;
    priority?: string;
    overdue?: boolean;
    limit?: number;
    offset?: number;
}) {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await taskRepository.findAll({
                filter: {
                    status: options?.status,
                    assignedTo: options?.assignedTo,
                    instanceId: options?.instanceId,
                    priority: options?.priority,
                    overdue: options?.overdue
                } as any,
                pagination: {
                    limit: options?.limit,
                    offset: options?.offset
                }
            });
            setTasks(data);
        } catch (err) {
            setError(err as Error);
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [options]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    return {
        tasks,
        loading,
        error,
        refetch: fetchTasks
    };
}

// Hook for single task
export function useTask(taskId: string | null) {
    const [task, setTask] = useState<Task | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchTask = useCallback(async () => {
        if (!taskId) {
            setTask(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await taskRepository.findById(taskId);
            setTask(data);
        } catch (err) {
            setError(err as Error);
            setTask(null);
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    useEffect(() => {
        fetchTask();
    }, [fetchTask]);

    return {
        task,
        loading,
        error,
        refetch: fetchTask
    };
}

// Hook for tasks assigned to user
export function useMyTasks(assigneeId: string | null) {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchMyTasks = useCallback(async () => {
        if (!assigneeId) {
            setTasks([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await taskRepository.findByAssignee(assigneeId);
            setTasks(data);
        } catch (err) {
            setError(err as Error);
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [assigneeId]);

    useEffect(() => {
        fetchMyTasks();
    }, [fetchMyTasks]);

    return {
        tasks,
        loading,
        error,
        refetch: fetchMyTasks
    };
}

// Hook for task mutations
export function useTaskMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const createTask = useCallback(async (taskData: {
        instanceId: string;
        name: string;
        description?: string;
        taskType: Task['taskType'];
        priority?: Task['priority'];
        assignedTo?: string;
        assignedTeam?: string;
        dueDate?: string;
        estimatedDuration?: number;
        dependencies?: string[];
        context?: Record<string, any>;
        formSchema?: Record<string, any>;
        approvalRequired?: boolean;
        escalationRules?: EscalationRule[];
    }): Promise<Task | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await taskRepository.create(taskData);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateTask = useCallback(async (
        taskId: string,
        updateData: Partial<Task>
    ): Promise<Task | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await taskRepository.update(taskId, updateData);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const startTask = useCallback(async (taskId: string): Promise<Task | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await taskRepository.start(taskId);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const completeTask = useCallback(async (
        taskId: string,
        formData?: Record<string, any>,
        comment?: string
    ): Promise<Task | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await taskRepository.complete(taskId, formData, comment);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const reassignTask = useCallback(async (
        taskId: string,
        newAssignee: string,
        reason?: string
    ): Promise<Task | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await taskRepository.reassign(taskId, newAssignee, reason);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const escalateTask = useCallback(async (
        taskId: string,
        escalateTo: string,
        reason: string
    ): Promise<Task | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await taskRepository.escalate(taskId, escalateTo, reason);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteTask = useCallback(async (taskId: string): Promise<boolean> => {
        setLoading(true);
        setError(null);

        try {
            const result = await taskRepository.delete(taskId);
            return result;
        } catch (err) {
            setError(err as Error);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        createTask,
        updateTask,
        startTask,
        completeTask,
        reassignTask,
        escalateTask,
        deleteTask,
        loading,
        error
    };
}

// Hook for task metrics
export function useTaskMetrics(options?: {
    assignedTo?: string;
    instanceId?: string;
    startDate?: string;
    endDate?: string;
}) {
    const [metrics, setMetrics] = useState<TaskMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchMetrics = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await taskRepository.getMetrics(options);
            setMetrics(data);
        } catch (err) {
            setError(err as Error);
            setMetrics(null);
        } finally {
            setLoading(false);
        }
    }, [options]);

    useEffect(() => {
        fetchMetrics();
    }, [fetchMetrics]);

    return {
        metrics,
        loading,
        error,
        refetch: fetchMetrics
    };
}

// Hook for overdue tasks
export function useOverdueTasks(assigneeId?: string) {
    const [overdueTasks, setOverdueTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchOverdueTasks = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await taskRepository.findAll({
                filter: { overdue: true, assignedTo: assigneeId } as any,
                pagination: { limit: 50 }
            });
            setOverdueTasks(data);
        } catch (err) {
            setError(err as Error);
            setOverdueTasks([]);
        } finally {
            setLoading(false);
        }
    }, [assigneeId]);

    useEffect(() => {
        fetchOverdueTasks();
    }, [fetchOverdueTasks]);

    return {
        overdueTasks,
        loading,
        error,
        refetch: fetchOverdueTasks
    };
}

// Hook for task validation
export function useTaskValidation(task: Task | null) {
    const [isValid, setIsValid] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    useEffect(() => {
        if (!task) {
            setIsValid(false);
            setValidationErrors([]);
            return;
        }

        const errors: string[] = [];

        // Basic validation
        if (!task.name?.trim()) {
            errors.push('Task name is required');
        }

        if (!task.instanceId?.trim()) {
            errors.push('Instance ID is required');
        }

        // Date validation
        if (task.dueDate) {
            const dueDate = new Date(task.dueDate);
            const now = new Date();

            if (dueDate <= now && task.status === 'pending') {
                errors.push('Due date cannot be in the past for pending tasks');
            }
        }

        // Form validation
        if (task.formSchema && task.status === 'completed') {
            if (!task.formData) {
                errors.push('Form data is required for completed tasks with form schema');
            }
        }

        // Dependency validation
        if (task.dependencies && task.dependencies.length > 0) {
            // This would require additional API calls to validate dependencies
            // For now, just check format
            const invalidDeps = task.dependencies.filter(dep => !dep || typeof dep !== 'string');
            if (invalidDeps.length > 0) {
                errors.push('Invalid dependency IDs found');
            }
        }

        setValidationErrors(errors);
        setIsValid(errors.length === 0);
    }, [task]);

    return {
        isValid,
        validationErrors
    };
}